import type { DropoffTimelineEvent } from '../types'

import { WarningOutlined } from '@ant-design/icons'
import { Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { Fragment } from 'react/jsx-runtime'

import '../index.css'

import { reportTypeNameMapper } from '../constants'

const DropoffTypeNameMapper: Record<string, string> = {
  sftp: 'SFTP',
  smb: 'SMB'
}

const DropoffPlinkErrorTooltip = (props: { event: DropoffTimelineEvent }): JSX.Element => {
  const { event } = props

  return (
    <div className='text-lucent-text flex flex-col gap-2 px-3 py-2'>
      {event.data.dropoff_type === 'sftp' && (
        <>
          <p className='flex flex-row'>
            <strong>plink Execution Error</strong>
          </p>
          {event.data.lin_timedout === true && (
            <p>
              <Tag color='red' className='font-lato size-xs'>
                Timed out
              </Tag>
            </p>
          )}
          <p>{event.data.lin_error}</p>
        </>
      )}
    </div>
  )
}

const DropoffErrorTooltip = (props: { event: DropoffTimelineEvent }): JSX.Element => {
  const { event } = props

  const data = [
    { label: 'Destination', value: event.data.destination },
    { label: 'Path', value: `${event.data.folder}/${event.data.name}` },
    { label: 'Error', value: event.data.error }
  ]

  return (
    <div className='text-lucent-text flex flex-col gap-2 px-3 py-2'>
      <strong>{DropoffTypeNameMapper[event.data.dropoff_type]} drop off failure</strong>
      <div className='flex flex-col gap-1'>
        {data.map(d => (
          <Fragment key={d.label}>
            <span className='font-lato text-[0.625rem] uppercase font-semibold text-gray-500 text-left'>
              {d.label}
            </span>
            <span className='font-lato text-[0.625rem] font-semibold text-lucent-text text-left'>
              {d.value}
            </span>
          </Fragment>
        ))}
      </div>
    </div>
  )
}

export const DropoffTimeline = (props: { event: DropoffTimelineEvent }): JSX.Element => {
  const { event } = props

  switch (event.data.status) {
    case 'scheduled': {
      return (
        <p className='max-w-full'>
          <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
            {reportTypeNameMapper[event.data.report_type] ?? event.data.report_type}
          </span>
          {' report will be dropped off to '}
          <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
            {DropoffTypeNameMapper[event.data.dropoff_type]}
          </span>
          {event.data.scheduled_at != null && event.data.scheduled_at !== '' ? (
            <>
              {' at '}
              <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
                {dayjs(event.data.scheduled_at).format('MM/DD HH:mm')}
              </span>
            </>
          ) : (
            <> now</>
          )}
        </p>
      )
    }
    case 'failed': {
      return (
        <p className='max-w-full'>
          <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
            {reportTypeNameMapper[event.data.report_type] ?? event.data.report_type}
          </span>
          {' report drop off to '}
          <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
            {DropoffTypeNameMapper[event.data.dropoff_type]}
          </span>
          {' failed '}
          <Tooltip color='#ffffff' title={<DropoffErrorTooltip event={event} />}>
            <WarningOutlined className='text-red-500 text-sm ml-1.5' />
          </Tooltip>
        </p>
      )
    }
    default: {
      return (
        <p className='max-w-full'>
          <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
            {reportTypeNameMapper[event.data.report_type] ?? event.data.report_type}
          </span>
          {' report dropped off to '}
          <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
            {DropoffTypeNameMapper[event.data.dropoff_type]}
          </span>
          {' as '}
          <span className='text-xs text-lucent-text rounded bg-gray-100 font-mono p-1 -m-1 mx-0'>
            {event.data.name}
          </span>{' '}
          {event.data.lin_error != null && event.data.lin_error !== '' && (
            <Tooltip color='#ffffff' title={<DropoffPlinkErrorTooltip event={event} />}>
              <WarningOutlined className='text-yellow-500 text-sm ml-1.5' />
            </Tooltip>
          )}
        </p>
      )
    }
  }
}
